import { useState } from "react";
import { Search, Plus, UserCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "unassigned", label: "Unassigned" },
  { id: "resolved", label: "Resolved" },
];

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

export default function ConversationList({ convs, selected, onSelect, filter, setFilter, search, setSearch, onStartChat, isAdmin, agents = [], agentFilter = "", setAgentFilter }) {
  const [newOpen, setNewOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  return (
    <div className="w-80 shrink-0 border-r-2 flex flex-col bg-white/50" style={{ borderColor: "var(--cd-navy)" }}>
      <div className="p-3 border-b-2" style={{ borderColor: "var(--cd-navy)" }}>
        <div className="flex items-center gap-2 mb-2.5">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input data-testid="inbox-search-input" className="input-tactile pl-8 py-1.5 text-sm" placeholder="Search name or phone..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button data-testid="new-chat-btn" className="btn-yellow px-2.5 py-1.5" onClick={() => setNewOpen(true)} title="New chat">
            <Plus size={16} />
          </button>
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className="text-[11px] font-bold px-2.5 py-1 rounded-full border-2 transition-colors"
              style={filter === f.id ? { background: "var(--cd-navy)", color: "#fff", borderColor: "var(--cd-navy)" } : { borderColor: "var(--cd-navy)", background: "#fff" }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {isAdmin && (
          <select
            data-testid="agent-filter-select"
            className="input-tactile mt-2 w-full py-1 text-xs font-semibold"
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            title="Filter chats by agent"
          >
            <option value="">👥 All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.online ? "🟢" : "⚪"} {a.name}{a.role === "admin" ? " (admin)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
        {convs.length === 0 && (
          <div className="p-5 text-center">
            <div className="font-hand text-lg text-amber-800">no conversations yet...</div>
            <div className="text-xs text-slate-500 mt-1">Incoming WhatsApp messages appear here, or start a new chat.</div>
          </div>
        )}
        {convs.map((c) => (
          <button
            key={c.id}
            data-testid={`conversation-item-${c.phone}`}
            onClick={() => onSelect(c)}
            className={`w-full text-left px-3 py-2.5 border-b flex gap-2.5 items-start transition-colors ${selected?.id === c.id ? "bg-[var(--cd-yellow)]" : "hover:bg-black/5"}`}
            style={{ borderColor: "#E5DCB8" }}
          >
            <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-xs font-bold border-2 bg-white" style={{ borderColor: "var(--cd-navy)" }}>
              {(c.contact_name || c.phone || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm truncate">{c.contact_name || c.phone}</span>
                <span className="text-[10px] text-slate-500 font-semibold shrink-0">{timeAgo(c.last_message_at)}</span>
              </div>
              <div className="text-xs text-slate-600 truncate">{c.last_message || "—"}</div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {c.assigned_to_name && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5" style={{ background: "var(--cd-blue)", border: "1px solid var(--cd-navy)" }}>
                    <UserCheck size={9} /> {c.assigned_to_name.split(" ")[0]}
                  </span>
                )}
                {c.status === "resolved" && <span className="status-badge badge-approved">resolved</span>}
                {c.window_open ? (
                  <span className="text-[9px] font-bold text-green-700">● 24h open</span>
                ) : (
                  <span className="text-[9px] font-bold text-slate-400">○ window closed</span>
                )}
              </div>
            </div>
            {c.unread_count > 0 && (
              <span className="shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: "#25D366" }} data-testid="unread-badge">
                {c.unread_count}
              </span>
            )}
          </button>
        ))}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="card-tactile card-yellow border-2" style={{ borderColor: "var(--cd-navy)" }}>
          <DialogHeader><DialogTitle className="font-heading">Start New Chat</DialogTitle></DialogHeader>
          <label className="label-upper">WhatsApp Number (with country code)</label>
          <input data-testid="new-chat-phone-input" className="input-tactile" placeholder="e.g. 919958709623" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <label className="label-upper mt-2">Name (optional)</label>
          <input data-testid="new-chat-name-input" className="input-tactile" placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="text-xs text-slate-600 mt-1">New chats outside the 24h window must begin with an approved template.</div>
          <button data-testid="new-chat-submit-btn" className="btn-navy mt-2 justify-center" disabled={!phone.trim()} onClick={() => { onStartChat(phone, name); setNewOpen(false); setPhone(""); setName(""); }}>
            Open Conversation
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
